import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { attrRicerca, fmtData } from "@/lib/listaColonne";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeftRight } from "lucide-react";
import { errore, fmtTelefono } from "./crmComuni";

// Sprint CRM-2A — unione di due contatti: resta quello da cui si parte (invertibile), valori scelti uno per uno
const CAMPI = { nome: "Nome", altri_recapiti: "Altri recapiti", consenso: "Consenso" };
const mostra = (k, v) => {
  if (k !== "consenso") return v || "—";
  return v ? `${v.valore ? "Sì" : "No"} · registrato il ${fmtData(v.data)}` : "Non registrato";
};
const quando = (iso) => (iso ? `il ${new Date(iso).toLocaleDateString("it-IT")} alle ${new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "in un momento non registrato");

function CercaContatto({ c, onScegli }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/crm/contatti?company_id=${c.company_id}&page_size=20${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`)
      .then((r) => setLista(r.data.items.filter((x) => x.id !== c.id))).catch(() => setLista([])), 250);
    return () => clearTimeout(t);
  }, [q, c.id, c.company_id]);
  return (
    <div className="space-y-1" data-testid="crm-unisci-cerca">
      <Input {...attrRicerca("crm-unisci")} placeholder="Cerca il contatto da unire" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-unisci-cerca-input" />
      <div className="max-h-48 overflow-auto border rounded">
        {lista.map((x) => <button key={x.id} type="button" onClick={() => onScegli(x.id)} className="block w-full text-left px-2 py-1 text-sm hover:bg-sky-50"
          data-testid={`crm-unisci-scegli-${x.id}`}>{x.nome} · {[...(x.telefoni || []).map(fmtTelefono), ...(x.email || [])].join(", ") || "—"}</button>)}
        {!lista.length && <div className="px-2 py-1 text-xs text-slate-400">Nessun contatto</div>}
      </div>
    </div>
  );
}

function Confronto({ d, scelte, setScelte }) {
  const tel = [...new Set([...(d.resta.telefoni || []), ...(d.elimina.telefoni || [])])];
  const mail = [...new Set([...(d.resta.email || []), ...(d.elimina.email || [])])];
  return (
    <div className="space-y-3">
      {d.campi_diversi.map((k) => (
        <div key={k} className="border rounded p-2" data-testid={`crm-unisci-campo-${k}`}>
          <div className="text-sm font-medium mb-1">{CAMPI[k]}</div>
          <div className="grid grid-cols-2 gap-2">
            {["resta", "elimina"].map((chi) => (
              <button key={chi} type="button" onClick={() => setScelte({ ...scelte, [k]: chi })} data-testid={`crm-unisci-${k}-${chi}`}
                className={`text-left rounded border px-2 py-1.5 text-sm transition-colors ${scelte[k] === chi ? "border-sky-500 bg-sky-50 ring-1 ring-sky-400" : "border-slate-200 hover:bg-slate-50"}`}>
                {mostra(k, d[chi][k])}</button>
            ))}
          </div>
        </div>
      ))}
      <div className="text-sm text-slate-600" data-testid="crm-unisci-recapiti">Telefoni ed email si uniscono: {[...tel.map(fmtTelefono), ...mail].join(", ") || "—"}</div>
    </div>
  );
}

export default function UnisciContatti({ c, onChiudi }) {
  const nav = useNavigate();
  const [coppia, setCoppia] = useState(null);  // [resta, elimina]
  const [d, setD] = useState(null);
  const [scelte, setScelte] = useState({});
  const [negato, setNegato] = useState("");
  const [conflitti, setConflitti] = useState(null);
  useEffect(() => {
    if (!coppia) return;
    setD(null); setScelte({}); setNegato(""); setConflitti(null);
    api.get(`/crm/contatti/${coppia[0]}/unione?altro_id=${coppia[1]}`).then((r) => {
      // Sprint CRM-3 — resta quello collegato all'anagrafica
      if (r.data.resta_obbligato && r.data.resta_obbligato !== coppia[0]) { setCoppia([coppia[1], coppia[0]]); return; }
      setD(r.data);
    }).catch((e) => setNegato(errore(e)));
  }, [coppia]);
  const unisci = async () => {
    const orig = (x) => Object.fromEntries(d.campi_congelati.map((k) => [k, x[k] ?? null]));
    try {
      const r = await api.post(`/crm/contatti/${d.resta.id}/unione`, { altro_id: d.elimina.id, scelte, originali: { resta: orig(d.resta), elimina: orig(d.elimina) } });
      toast.success("Contatti uniti"); onChiudi(); nav(`/crm/contatti/${r.data.id}`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.conflitti) setConflitti(det); else toast.error(errore(e));
    }
  };
  const pronti = d && d.campi_diversi.every((k) => scelte[k]);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crm-unisci-dialog">
        <DialogHeader><DialogTitle>Unisci con…</DialogTitle></DialogHeader>
        {!coppia && <CercaContatto c={c} onScegli={(id) => setCoppia([c.id, id])} />}
        {negato && <div className="rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800" data-testid="crm-unisci-negato">{negato}</div>}
        {d && !conflitti && <>
          <div className="flex items-center justify-between gap-2 rounded bg-slate-100 px-3 py-2 text-sm" data-testid="crm-unisci-chi">
            <span>Resta: <b data-testid="crm-unisci-resta">{d.resta.nome}</b> · Viene eliminato: <b data-testid="crm-unisci-elimina">{d.elimina.nome}</b></span>
            <Button size="sm" variant="outline" onClick={() => setCoppia([coppia[1], coppia[0]])} data-testid="crm-unisci-inverti"
              className={d.resta_obbligato ? "hidden" : ""}>
              <ArrowLeftRight className="h-4 w-4 mr-1" /> Inverti</Button>
          </div>
          <Confronto d={d} scelte={scelte} setScelte={setScelte} />
        </>}
        {conflitti && (
          <div className="space-y-2" data-testid="crm-unisci-conflitti">
            <div className="flex items-center gap-2 font-medium text-amber-900"><AlertTriangle className="h-4 w-4" /> {conflitti.messaggio}</div>
            {conflitti.conflitti.map((x) => (
              <div key={`${x.contatto}-${x.campo}`} className="border rounded p-2 text-sm" data-testid={`crm-unisci-conflitto-${x.campo}`}>
                <b>{x.contatto}</b> · {CAMPI[x.campo] || x.campo}: {Array.isArray(x.valore_attuale) ? x.valore_attuale.join(", ") : mostra(x.campo, x.valore_attuale)}
                <div className="text-xs text-slate-500">{x.autore_registrato ? `Modificato da ${x.modificato_da || "utente sconosciuto"} ${quando(x.modificato_il)}`
                  : `Modificato ${quando(x.modificato_il)}, autore non registrato`}</div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} data-testid="crm-unisci-annulla">{conflitti ? "Chiudi" : "Annulla"}</Button>
          {d && !conflitti && <Button onClick={unisci} disabled={!pronti} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-unisci-conferma">Unisci</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
